import {
    ApplicationResponseStatus,
    BoundaryPartRequestBoundaryType
} from 'generatedTypes/flight/models';
import { useCallback, useMemo, useRef, useState } from 'react';

import useApplications from 'hooks/useApplications';
import useBoundary from 'hooks/useBoundary';
import usePageRoutes from 'hooks/usePageRoutes';

import { APPLICATION_PAGE_SIZE, CARD_COUNT, FOCUS_APPLICATION_COUNT } from './constants';
import type {
    ApplicationResponse,
    BoundaryPartData,
    CardItems,
    GenerateCards,
    Geometry,
    Normalize
} from './types';

interface DataWithGeometry {
    data: CardItems[];
    focusGeometry: Geometry | undefined;
    geometry: Geometry;
}

const useData = () => {
    const { applications = [], fetch, isLoading: isLoadingApplications } = useApplications();
    const { fetchBoundary } = useBoundary();
    const [hasMore, setHasMore] = useState(false);
    const [isLoadingBoundaries, setIsLoadingBoundaries] = useState(false);
    const [dataWithGeometry, setDataWithGeometry] = useState<DataWithGeometry | undefined>();
    const fetchedBoundaryIds = useRef(new Set<string>());
    const boundaryPartsByIdRef = useRef<Record<string, BoundaryPartData[]>>({});

    const { applicationDetailsPage } = usePageRoutes();

    const generateCards: GenerateCards = useCallback(
        () =>
            [...Array(CARD_COUNT)].reduce((accumulator, _, index) => {
                const date = new Date();

                date.setDate(date.getDate() - index);

                accumulator.push({
                    date,
                    items: []
                });

                return accumulator;
            }, []),
        []
    );

    const normalize: Normalize = useCallback(
        (applicationsToNormalize: ApplicationResponse[]) =>
            applicationsToNormalize.reduce((accumulator, item) => {
                const { createdAt, id } = item;

                const createdDate = new Date(createdAt);

                const compareDate = ({ date }: { date: Date }) =>
                    date.toLocaleDateString('en-US') === createdDate.toLocaleDateString('en-US');

                let dateIndex = accumulator.findIndex(compareDate);

                if (dateIndex === -1) {
                    accumulator.push({ date: createdDate, items: [] });
                    dateIndex = accumulator.length - 1;
                }

                accumulator[dateIndex].items.push({
                    ...item,
                    detailsUrl: applicationDetailsPage(id)
                });

                return accumulator;
            }, generateCards()),
        [applicationDetailsPage, generateCards]
    );

    const buildGeometryData = useCallback(
        (
            currentApplications: ApplicationResponse[],
            boundaryPartsById: Record<string, BoundaryPartData[]>
        ): DataWithGeometry => {
            const activeApplications = currentApplications
                .filter(
                    application =>
                        application.status === ApplicationResponseStatus.IN_PROGRESS ||
                        application.status === ApplicationResponseStatus.COMPLETE
                )
                .sort(
                    (first, second) =>
                        new Date(second.createdAt).getTime() - new Date(first.createdAt).getTime()
                );

            const focusBoundaryIds = new Set(
                activeApplications
                    .slice(0, FOCUS_APPLICATION_COUNT)
                    .map(application => application.boundaryId)
                    .filter((id): id is string => Boolean(id))
            );

            const boundaryParts = Object.values(boundaryPartsById).flat();

            const focusBoundaryParts = Object.entries(boundaryPartsById)
                .filter(([id]) => focusBoundaryIds.has(id))
                .flatMap(([, parts]) => parts);

            const geometry: Geometry = { isEditable: false, parts: boundaryParts };

            const focusGeometry =
                focusBoundaryParts.length > 0
                    ? { isEditable: false, parts: focusBoundaryParts }
                    : undefined;

            return {
                data: normalize(currentApplications),
                focusGeometry,
                geometry
            };
        },
        [normalize]
    );

    const fetchBoundariesForApplications = useCallback(
        async (currentApplications: ApplicationResponse[]) => {
            const newApplications = currentApplications.filter(
                application =>
                    application.boundaryId &&
                    !fetchedBoundaryIds.current.has(application.boundaryId)
            );

            if (newApplications.length === 0) {
                return boundaryPartsByIdRef.current;
            }

            const newBoundaryIds = newApplications.map(
                application => application.boundaryId as string
            );

            for (const id of newBoundaryIds) {
                fetchedBoundaryIds.current.add(id);
            }

            const boundaryIdToApplication = new Map(
                newApplications.map(application => [
                    application.boundaryId as string,
                    { applicationId: application.id, applicationName: application.name }
                ])
            );

            const boundaries = await Promise.all(newBoundaryIds.map(id => fetchBoundary({ id })));

            const newPartsById: Record<string, BoundaryPartData[]> = {};

            newBoundaryIds.forEach((id, index) => {
                const application = boundaryIdToApplication.get(id);

                newPartsById[id] = (boundaries[index]?.boundaryParts ?? []).map(part => ({
                    ...part,
                    applicationId: application?.applicationId,
                    applicationName:
                        part.boundaryType === BoundaryPartRequestBoundaryType.APPLY
                            ? application?.applicationName
                            : undefined
                }));
            });

            boundaryPartsByIdRef.current = { ...boundaryPartsByIdRef.current, ...newPartsById };

            return boundaryPartsByIdRef.current;
        },
        [fetchBoundary]
    );

    const fetchApplications = useCallback(async () => {
        setIsLoadingBoundaries(true);

        try {
            const { done, value: fetchedApplications = [] } = await fetch({
                pageSize: APPLICATION_PAGE_SIZE
            }).next();

            setHasMore(!done);

            const allApplications = [...applications, ...fetchedApplications];
            const boundaryPartsById = await fetchBoundariesForApplications(allApplications);

            setDataWithGeometry(buildGeometryData(allApplications, boundaryPartsById));
        } finally {
            setIsLoadingBoundaries(false);
        }
    }, [applications, buildGeometryData, fetch, fetchBoundariesForApplications]);

    const isLoading = isLoadingApplications || isLoadingBoundaries;

    const emptyGeometry: Geometry = useMemo(() => ({ isEditable: false, parts: [] }), []);

    return {
        data: dataWithGeometry?.data ?? normalize([]),
        fetch: fetchApplications,
        focusGeometry: dataWithGeometry?.focusGeometry,
        geometry: dataWithGeometry?.geometry ?? emptyGeometry,
        handleNextPage: hasMore ? fetchApplications : undefined,
        isLoading
    };
};

export default useData;
